import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LevelBadge } from "@/components/LevelBadge";
import { FinePrint } from "@/components/FinePrint";
import { EMBLEM, LSC_LOGO } from "@/lib/assets";
import { DOMAINS, SKILLS } from "@/lib/framework";
import {
  RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  AreaChart, Area,
} from "recharts";
import { Briefcase, Star, ClipboardCheck, Loader2, Route as RouteIcon } from "lucide-react";

const CAMPUS = "Lone Star College – Tomball";
const TERM = "Spring 2026";

const RANGE_KEY = [
  { label: "Exemplary", range: "90–100%", hex: "#10b981" },
  { label: "Career Ready", range: "80–89%", hex: "#0d9488" },
  { label: "Competent", range: "70–79%", hex: "#2563eb" },
  { label: "Needs Development", range: "0–69%", hex: "#d97706" },
];

function Bar_({ children }) {
  return (
    <div className="mt-8 mb-4 rounded-md bg-[#003057] px-4 py-2">
      <h2 className="font-heading text-sm font-bold uppercase tracking-[0.15em] text-white">{children}</h2>
    </div>
  );
}

function PctGauge({ pct, hex }) {
  const data = [{ value: pct, fill: hex }];
  return (
    <div className="relative flex items-center justify-center" style={{ width: 170, height: 170 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart innerRadius="76%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
          <RadialBar background={{ fill: "#e2e8f0" }} dataKey="value" cornerRadius={12} angleAxisId={0} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono-num text-3xl font-bold text-[#003057]">{pct}%</span>
      </div>
    </div>
  );
}

function Dots({ pct, color }) {
  const filled = Math.max(1, Math.min(5, Math.round(pct * 5)));
  return (
    <span className="flex gap-1">
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: i < filled ? color : "#e2e8f0" }} />
      ))}
    </span>
  );
}

function strengthLabel(pct) {
  if (pct >= 0.85) return "Strong";
  if (pct >= 0.7) return "Solid";
  if (pct >= 0.55) return "Developing";
  return "Growth area";
}

export default function EmploymentProfile() {
  const [fields, setFields] = useState([]);
  const [fieldId, setFieldId] = useState("");
  const [homeFieldId, setHomeFieldId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/career-fields").then(({ data }) => setFields(data));
    api.get("/student/me").then(({ data }) => {
      setFieldId(data.chosen_field || "general");
      setHomeFieldId(data.chosen_field || "general");
    });
  }, []);

  useEffect(() => {
    if (!fieldId) return;
    setLoading(true);
    api.get("/employment-profile", { params: { field_id: fieldId } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [fieldId]);

  const orderedFields = [
    ...fields.filter((f) => f.id === fieldId),
    ...fields.filter((f) => f.id !== fieldId).sort((a, b) => a.name.localeCompare(b.name)),
  ];

  const domainSummary = data
    ? DOMAINS.map((d) => {
        const rws = data.rows.filter((r) => SKILLS.find((s) => s.id === r.id)?.domain === d.id);
        const weighted = rws.reduce((a, r) => a + r.weighted_score, 0);
        const max = rws.reduce((a, r) => a + r.weight, 0);
        const pct = max ? weighted / max : 0;
        return { ...d, weighted: weighted.toFixed(2), max: max.toFixed(2), pct, strength: strengthLabel(pct) };
      })
    : [];

  return (
    <DashboardShell title="Employment Readiness Report" nav={STUDENT_NAV}>
      {/* Branded report header banner */}
      <div className="mb-6 overflow-hidden rounded-xl brand-hero">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-3">
            <img src={LSC_LOGO} alt="Lone Star College" className="h-11 w-auto rounded-md bg-white px-2 py-1.5" />
            <div>
              <p className="font-heading text-lg font-extrabold uppercase tracking-wide text-white">
                LSC Transferable Skills Employment Readiness Report
              </p>
              {data && (
                <p className="text-xs text-slate-300">
                  {data.student.name} &nbsp;▪&nbsp; {data.student.program} &nbsp;▪&nbsp; {CAMPUS} &nbsp;▪&nbsp; {TERM}
                </p>
              )}
            </div>
          </div>
          {data && (
            <p className="text-sm font-semibold text-white">
              Chosen Field: <span className="text-[#a8e063]">{data.field.name}</span>
            </p>
          )}
        </div>
      </div>

      {/* Field selector */}
      <Card className="mb-2 p-5 card-elevate">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[260px] flex-1">
            <p className="mb-1.5 flex items-center gap-2 text-sm font-bold text-[#003057]">
              <Briefcase className="h-4 w-4 text-[#78be21]" /> Chosen career field
            </p>
            <p className="mb-2 text-xs text-slate-500">
              Current degree plan: <span className="font-semibold text-[#003057]">{data ? data.student.program : "…"}</span>
            </p>
            <Select value={fieldId} onValueChange={setFieldId}>
              <SelectTrigger data-testid="employment-field-dropdown" className="w-full"><SelectValue placeholder="Select a career field" /></SelectTrigger>
              <SelectContent>
                {orderedFields.map((f) => (
                  <SelectItem key={f.id} value={f.id} data-testid={`employment-field-${f.id}`}>
                    {f.id === homeFieldId ? `★ ${f.name} (your chosen plan)` : f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="max-w-md text-xs text-slate-500">
            Your skill scores stay the same — each field weights the 10 skills differently, so your composite
            benchmark recomputes for the field you choose.
          </p>
        </div>
      </Card>

      {(!data || loading) && (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>
      )}

      {data && !loading && (
        <div data-testid="employment-profile-body">
          <Bar_>Employment Readiness Summary</Bar_>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
            <Card className="p-5 text-center card-elevate">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Career Readiness Rating</p>
              <p className="mt-2 font-heading text-2xl font-extrabold" style={{ color: data.rating.hex }} data-testid="employment-rating">{data.rating.label}</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">
                Weighted performance across assessed transferable skills for {data.field.name}.
              </p>
            </Card>
            <Card className="flex flex-col items-center p-5 card-elevate">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">TSB Score</p>
              <PctGauge pct={data.tsb_pct} hex={data.rating.hex} />
              <div className="w-full space-y-0.5">
                {RANGE_KEY.map((r) => (
                  <div key={r.label} className="flex items-center justify-between text-[11px]">
                    <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.hex }} />{r.label}</span>
                    <span className="font-mono-num text-slate-400">{r.range}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5 card-elevate">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400"><Star className="h-3.5 w-3.5 text-[#78be21]" /> Strengths Highlight</p>
              <div className="mt-3 space-y-2">
                {data.strengths.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    <span className="font-semibold text-[#003057]">{s.skill}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-slate-400">Key transferable skills demonstrating your strengths for the chosen field.</p>
            </Card>
            <Card className="p-5 card-elevate">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400"><ClipboardCheck className="h-3.5 w-3.5 text-[#78be21]" /> Assessment Coverage</p>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between"><span className="text-slate-600">Skills Assessed</span><span className="font-mono-num font-bold text-[#003057]">{data.coverage.assessed} of {data.coverage.total}</span></div>
                <div className="flex items-center justify-between"><span className="text-slate-600">Coverage Rate</span><span className="font-mono-num font-bold text-[#003057]">{data.coverage.rate}%</span></div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-2"><span className="text-slate-600">Performance Index</span><span className="font-mono-num font-bold text-[#005291]" data-testid="employment-tsb-pct">{data.performance_index}%</span></div>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">TSB Score = Performance Index × Coverage Rate ({data.performance_index}% × {data.coverage.rate}%)</p>
            </Card>
          </div>

          <Bar_>Job Task Readiness · {data.field.name}</Bar_>
          <Card className="overflow-x-auto p-2 card-elevate sm:p-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job Task Category</TableHead>
                  <TableHead>Transferable Skill</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead>Band</TableHead>
                  <TableHead className="text-right">Weight</TableHead>
                  <TableHead className="text-right">Weighted</TableHead>
                  <TableHead className="min-w-[220px]">Employer Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={r.id} data-testid={`jobtask-row-${i}`}>
                    <TableCell className="font-semibold text-[#003057]">{r.category}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.skill}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold">{r.score_pct}%</TableCell>
                    <TableCell><LevelBadge level={r.band_id} /></TableCell>
                    <TableCell className="text-right font-mono-num text-slate-600">{r.weight}%</TableCell>
                    <TableCell className="text-right font-mono-num font-bold text-[#005291]">{r.weighted_score.toFixed(2)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.note}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-[#003057]/5">
                  <TableCell colSpan={4} className="font-heading font-bold text-[#003057]">Total Transferable Skills Benchmark (TSB) Score</TableCell>
                  <TableCell className="text-right font-mono-num font-bold text-slate-600">100%</TableCell>
                  <TableCell className="text-right font-mono-num text-lg font-bold text-[#78be21]">{data.tsb_pct.toFixed(1)}</TableCell>
                  <TableCell className="text-[11px] text-slate-500">Weights reflect the importance of each skill for {data.field.name}.</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>

          <Bar_>Domain Summary (Based on Weighted Scores)</Bar_>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {domainSummary.map((d) => (
              <Card key={d.id} className="p-5 card-elevate" style={{ borderTop: `4px solid ${d.color}` }}>
                <p className="font-heading text-base font-bold text-[#003057]">{d.name}</p>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="font-mono-num text-2xl font-bold" style={{ color: d.color }}>{d.weighted}</p>
                    <p className="text-[11px] text-slate-400">of {d.max} weighted</p>
                  </div>
                  <div className="text-right">
                    <Dots pct={d.pct} color={d.color} />
                    <p className="mt-1 text-xs font-bold" style={{ color: d.color }}>{d.strength}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <Bar_>Career Field Weighting & Benchmark Comparison</Bar_>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="p-6 card-elevate">
              <p className="mb-3 font-heading text-sm font-bold text-[#003057]">Weighting · {data.field.name}</p>
              <div className="space-y-1.5">
                {[...data.weights].sort((a, b) => b.weight - a.weight).map((w) => (
                  <div key={w.id} className="flex items-center gap-3">
                    <span className="w-40 flex-shrink-0 truncate text-sm text-slate-600">{w.skill}</span>
                    <div className="h-2.5 flex-1 rounded-full bg-slate-100">
                      <div className="h-2.5 rounded-full bg-[#005291]" style={{ width: `${(w.weight / 20) * 100}%` }} />
                    </div>
                    <span className="w-10 text-right font-mono-num text-sm font-bold text-[#003057]">{w.weight}%</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-6 card-elevate">
              <p className="mb-1 font-heading text-sm font-bold text-[#003057]">Benchmark Comparison</p>
              <p className="mb-2 text-[11px] text-slate-500">Your weighted score vs. the LSC {data.field.name} cohort benchmark.</p>
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={data.benchmark} layout="vertical" margin={{ left: 20, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                    <YAxis type="category" dataKey="skill" width={110} tick={{ fontSize: 9, fill: "#475569" }} />
                    <Tooltip formatter={(v) => Number(v).toFixed(2)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="your_weighted" name="You" fill="#78be21" radius={[0, 4, 4, 0]} barSize={9} />
                    <Bar dataKey="benchmark_weighted" name="LSC benchmark" fill="#005291" radius={[0, 4, 4, 0]} barSize={9} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <Bar_>Growth Over Time & Career Pathways</Bar_>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Card className="p-6 card-elevate lg:col-span-2">
              <p className="mb-2 font-heading text-sm font-bold text-[#003057]">Composite TSB (Weighted) Score History</p>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <AreaChart data={data.growth} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#78be21" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#78be21" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                    <XAxis dataKey="term" tick={{ fontSize: 12, fill: "#475569" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Area type="monotone" dataKey="tsb_pct" name="Composite TSB %" stroke="#5fa114" strokeWidth={3} fill="url(#g)" dot={{ r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {data.growth_delta > 0 && (
                <p className="mt-2 text-sm font-semibold text-emerald-600">
                  You've improved +{data.growth_delta} points since {data.growth[0].term}. Keep building your strengths!
                </p>
              )}
            </Card>
            <Card className="p-6 card-elevate">
              <p className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-[#003057]"><RouteIcon className="h-4 w-4 text-[#78be21]" /> Career Pathways</p>
              <div className="flex flex-wrap gap-2">
                {data.field.roles.map((r) => (
                  <span key={r} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-[#003057]">{r}</span>
                ))}
              </div>
              <p className="mt-4 text-sm font-medium text-slate-500">Many careers. One strong skill foundation.</p>
            </Card>
          </div>

          {/* Footer bar */}
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#00243f] px-5 py-4 text-xs text-slate-300">
            <span>This profile is based on demonstrated evidence in assessed coursework and learning experiences.</span>
            <span>Report Date: May 20, 2026 &nbsp;·&nbsp; Learn more at lonestar.edu/gpa-plus</span>
          </div>
          <FinePrint className="mt-3">
            Figures are illustrative and computed on mock data. Field weightings are modeled on O*NET analyst skill-importance
            ratings and America Succeeds durable-skills demand; weights sum to 100% per field. Readiness percentages indicate
            skill-to-role fit, not a prediction of hiring or performance. See Research & Methodology for full sources and limitations.
          </FinePrint>
        </div>
      )}
    </DashboardShell>
  );
}
